/**
 * Paid-access gate — how the paid launch gates AUTHENTICATED users with no
 * active entitlement (trial expired, subscription canceled, or signed up
 * without ever starting a trial). Wraps AuthenticatedApp in AuthGuard.
 *
 * The gate renders INSTEAD of the app for a user with no active entitlement,
 * hosting the SpendingToolsPaywall (which itself renders trial-available vs
 * winback copy from real eligibility). Paid-only since the 2026-07 cutover
 * (PAID_LAUNCH_PLAN.md §10); the `paid_launch_enabled` read path is gone.
 *
 * Deliberately fails OPEN in AMBIGUOUS states — a wall shown to a paying
 * customer is worse than a free session for a lapsed one:
 * - subscription state not ready yet → children (no flash for paying users);
 * - entitlement fetch errored (`status: 'unknown'`) → children — the accepted
 *   fail-open scenario (RevenueCat/entitlement backend not responding);
 * - purchased in this session → children immediately (optimistic — webhook
 *   entitlement may land a beat later).
 *
 * It fails CLOSED on web when the Stripe price env vars are missing (decided
 * 2026-07-19): that is a deploy misconfiguration, not an ambiguous user
 * state — failing open there hands every unentitled web user the full app
 * for free, silently. The gate holds, with a "membership options are
 * temporarily unavailable" state (contact/log-out escapes) until the
 * VITE_STRIPE_SPENDING_* env vars land.
 *
 * Who hits this at cutover: new signups who skipped the pre-login paywall,
 * trial-expired/canceled users, AND any returning legacy user who was NOT in
 * the grandfather grant cohort (the comp covers active-≤90d connected users;
 * dormant returners land here on the winback paywall — the intended
 * "no free path" outcome, but worth knowing when reading support tickets).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import {
  clearOrphanedPurchaseHint,
  isRestorableStoreForPlatform,
  readOrphanedPurchaseHint,
  webPricesConfigured,
} from '../../lib/paidLaunch';
import { getActiveEntitlements, restorePurchases } from '../../services/purchasesService';
import { getStoreOwnedSubscriptions, storeOwnsSubscription } from '../../services/storeEntitlementsService';
import { logError } from '@/lib/log';
import { recordPaywallFunnelEvent } from '@/lib/paywallFunnelEvents';
import { trackEvent } from '@shared/services/analyticsTracker';
import { emphasizeBrand } from '../../lib/emphasizeBrand';
import { SpendingToolsPaywall } from './SpendingToolsPaywall';
import { WebRestoreGuidance } from './WebRestoreGuidance';

const GATE_SOURCE = 'paid_access_gate';

export const PaidAccessGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, logout } = useAuth();
  const { isPremium, isReady, subscriptionInfo, refreshSubscription } = useSubscription();

  // Orphaned-purchase hint (native): the pre-login stranded-payer check saw an
  // active entitlement on this device's ANONYMOUS uid, and the user then signed
  // in to a different account. Their membership is one Restore tap away — lead
  // with that instead of a fresh sell. Hint only; restore is store-verified.
  // Settable because the STORE QUERY below outranks it: an authoritative
  // "this store account owns nothing" demotes a stale hint on the spot.
  const [restoreHint, setRestoreHint] = useState(
    () => Capacitor.isNativePlatform() && readOrphanedPurchaseHint() !== null,
  );
  const [restoring, setRestoring] = useState(false);
  const [restoreFailed, setRestoreFailed] = useState(false);

  // Ground truth from the device's STORE ACCOUNT (StoreEntitlements plugin):
  // does the signed-in Apple ID / Play account hold an active sub for this
  // app, regardless of which Firebase uid is in front of us? Unlike the
  // localStorage hint this survives reinstall/cleared storage, so it catches
  // the new-device case the hint never could. 'unavailable' (web, plugin
  // error, billing service down) degrades to exactly the pre-plugin behavior:
  // the hint machinery and the normal sell.
  const [storeCheck, setStoreCheck] = useState<'pending' | 'owned' | 'none' | 'unavailable'>(
    () => (Capacitor.isNativePlatform() ? 'pending' : 'unavailable'),
  );
  // How long the auto-open sell waits for the store query before proceeding —
  // the query reads an on-device cache, so it usually answers well inside
  // this. A late "owned" still flips the backdrop to the Restore lead.
  const STORE_CHECK_TIMEOUT_MS = 2_500;
  const [storeCheckTimedOut, setStoreCheckTimedOut] = useState(false);

  const canSellHere = Capacitor.isNativePlatform() || webPricesConfigured();

  /**
   * Which paywall treatment to show (Justin, 2026-07-21). This gate stands in
   * front of two very different people:
   *
   * - **Never had a membership on this account** → still ACQUISITION. Keep the
   *   green funnel treatment, continuous with the carousel and auth screen they
   *   just came through. Covers both the brand-new signup and the returning
   *   store user on a fresh account (existing subscription on a new device, a
   *   second account) — the funnel paywall drops its trial timeline for the
   *   latter, since the store won't be granting them one.
   * - **Membership LAPSED on this account** (winback / expired) → the white
   *   in-app treatment. They have been inside the product; dropping them back
   *   into onboarding chrome reads as a downgrade, not a welcome.
   *
   * `expirationDate` on a non-active subscription is exactly the record of
   * having had one, which is why it's the discriminator. Absent it, we treat
   * the person as new — the safer default, since showing acquisition framing to
   * a lapsed member is a smaller error than showing winback framing to someone
   * who has never subscribed.
   */
  const paywallSurface = subscriptionInfo?.expirationDate ? 'app' : 'funnel';

  // Auto-open the paywall when the gate shows — the backdrop screen below is
  // the fallback surface after a dismissal, not the primary ask. With a
  // restore signal (hint or store query), the backdrop leads instead (Restore
  // is the ask). On native the auto-open waits briefly for the store query,
  // so we never pitch a fresh sell to a store account that already owns the
  // sub. When this platform can't sell (web, prices unconfigured), there is
  // no paywall to open — the backdrop's unavailable state is the whole
  // surface. Driven by the effect below (not initial state) because the
  // store answer is async.
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [purchasedThisSession, setPurchasedThisSession] = useState(false);
  const purchasedRef = useRef(false);
  // The paywall has reached its "You're all set!" screen and the user has not
  // tapped Done yet. The entitlement webhook can land in that window (observed
  // 3–5s after the subscription callable on dev, 2026-09-07), flipping
  // `isPremium` and — without this — dissolving the gate mid-confirmation, so
  // the member is bounced into onboarding without ever seeing their receipt.
  // Hold the gate (and the open paywall) until Done closes it.
  const [successPending, setSuccessPending] = useState(false);

  // The DEVICE's RevenueCat user holds an active store entitlement (found by
  // the watcher effect below) — treat like an in-session purchase.
  const [deviceEntitlementFound, setDeviceEntitlementFound] = useState(false);

  // Failing because the WEB PRICES AREN'T CONFIGURED is a deploy
  // misconfiguration, not an ambiguous user state. The gate fails CLOSED on
  // it (unentitled web users are walled with no purchase path) — shout so
  // it's caught in monitoring immediately, not via support tickets.
  const misconfigLoggedRef = useRef(false);
  useEffect(() => {
    if (misconfigLoggedRef.current) return;
    if (!Capacitor.isNativePlatform() && !webPricesConfigured()
      && isReady && subscriptionInfo?.status !== 'unknown' && !isPremium) {
      misconfigLoggedRef.current = true;
      logError(new Error(
        'PaidAccessGate failing CLOSED: the web Stripe '
        + 'price env vars (VITE_STRIPE_SPENDING_MONTHLY_PRICE_ID / '
        + 'VITE_STRIPE_SPENDING_ANNUAL_PRICE_ID) are not configured — unentitled '
        + 'web users are gated with NO purchase path until the env vars land.',
      ), { context: 'paid_access_gate_misconfig' });
    }
  }, [isReady, subscriptionInfo?.status, isPremium]);

  const gated =
    isReady &&
    subscriptionInfo?.status !== 'unknown' &&
    !isPremium &&
    !purchasedThisSession &&
    !deviceEntitlementFound;

  // Native: ask the device's store account what it actually owns, once per
  // gate appearance. Ground truth beats the breadcrumb in both directions —
  // an active sub found here leads with Restore even with no hint (reinstall,
  // new device, cleared storage), and an authoritative empty answer demotes a
  // stale hint so we don't promise a restore the store would return empty.
  const storeCheckStarted = useRef(false);
  useEffect(() => {
    if (!gated || !Capacitor.isNativePlatform() || storeCheckStarted.current) return;
    storeCheckStarted.current = true;
    let cancelled = false;
    getStoreOwnedSubscriptions().then((result) => {
      if (cancelled) return;
      const outcome = !result.supported
        ? 'unavailable'
        : storeOwnsSubscription(result) ? 'owned' : 'none';
      setStoreCheck(outcome);
      trackEvent('paid_access_gate_store_check', {
        outcome,
        products: result.productIds.join(','),
      });
      if (outcome === 'none') {
        clearOrphanedPurchaseHint();
        setRestoreHint(false);
      }
    });
    return () => { cancelled = true; };
  }, [gated]);

  // The auto-open sell only waits so long for the store answer (see
  // STORE_CHECK_TIMEOUT_MS above).
  useEffect(() => {
    if (!gated || storeCheck !== 'pending') return;
    const timer = setTimeout(() => setStoreCheckTimedOut(true), STORE_CHECK_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [gated, storeCheck]);

  // Auto-open decision, once per gate appearance: open the sell unless a
  // restore signal leads (hint, or the store query finding an owned sub). If
  // the query later invalidates the hint, the backdrop's sell button is the
  // surface — no startling auto-open after the user has been reading.
  const autoOpenDecided = useRef(false);
  useEffect(() => {
    if (!gated || autoOpenDecided.current || !canSellHere) return;
    if (restoreHint) { autoOpenDecided.current = true; return; } // Restore leads
    if (storeCheck === 'pending' && !storeCheckTimedOut) return; // brief hold
    autoOpenDecided.current = true;
    if (storeCheck !== 'owned') setPaywallOpen(true);
  }, [gated, canSellHere, restoreHint, storeCheck, storeCheckTimedOut]);

  // Housekeeping: once THIS account verifiably holds the membership, any
  // orphaned-purchase hint has served its purpose (uid-preserving signup, a
  // completed restore, or the RC transfer landing). A stale hint would make a
  // FUTURE gate appearance (e.g. after the trial ends) falsely lead with
  // Restore.
  useEffect(() => {
    if (isReady && isPremium) clearOrphanedPurchaseHint();
  }, [isReady, isPremium]);

  // Native: while the gate is up, watch this device's RevenueCat user for an
  // active store entitlement. Covers the sign-in-to-existing-account path:
  // the store purchase parks on the pre-login anonymous uid, and when the SDK
  // re-posts the device receipt under the freshly-authed uid, RevenueCat
  // TRANSFERS the subscription to it ("Transfer to new App User ID") — but the
  // webhook → Firestore → snapshot pipeline takes ~30–60s, during which this
  // gate would pitch a paying customer a fresh sell. The SDK knows first, so
  // poll it briefly and dissolve the gate the moment the membership shows up
  // (optimistic, like a purchase — the entitlement snapshot catches up).
  // Store-restorable entitlements only: those are the ones RC's transfer can
  // actually move to this account via this device's receipts.
  useEffect(() => {
    if (!gated || !Capacitor.isNativePlatform()) return;
    let cancelled = false;
    let polls = 0;
    const MAX_POLLS = 24; // ~2 minutes — transfer latency cover, not a vigil

    const check = async () => {
      const entitlements = await getActiveEntitlements();
      if (cancelled) return;
      const restorable = entitlements.find((e) =>
        isRestorableStoreForPlatform(e.store, Capacitor.getPlatform()),
      );
      if (restorable) {
        cancelled = true;
        clearInterval(interval);
        clearOrphanedPurchaseHint();
        trackEvent('paid_access_gate_device_entitlement_found', { store: restorable.store });
        refreshSubscription();
        setDeviceEntitlementFound(true);
      }
    };

    const interval = setInterval(() => {
      polls += 1;
      if (polls >= MAX_POLLS) {
        clearInterval(interval);
        return;
      }
      check();
    }, 5_000);
    check();

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gated]);

  // One impression per gate appearance (not per re-render).
  const impressionFired = useRef(false);
  useEffect(() => {
    if (!gated) {
      impressionFired.current = false;
      return;
    }
    if (impressionFired.current) return;
    impressionFired.current = true;
    trackEvent('paid_access_gate_shown', { restoreHint });
    recordPaywallFunnelEvent(currentUser, 'impression', { source: GATE_SOURCE });
  }, [gated, currentUser, restoreHint]);

  if (!gated && !successPending) return <>{children}</>;

  const handleSuccess = () => {
    purchasedRef.current = true;
    setSuccessPending(true);
    recordPaywallFunnelEvent(currentUser, 'purchase_completed', { source: GATE_SOURCE });
  };

  const handleClose = () => {
    setPaywallOpen(false);
    setSuccessPending(false);
    if (purchasedRef.current) {
      // Let the user through right away; the entitlement snapshot catches up.
      setPurchasedThisSession(true);
    } else {
      recordPaywallFunnelEvent(currentUser, 'dismissed', { source: GATE_SOURCE });
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    setRestoreFailed(false);
    trackEvent('paid_access_gate_restore_attempted', {});
    const result = await restorePurchases();
    setRestoring(false);
    if (result.success && result.entitlements.length > 0) {
      // Store receipt verified — the subscription now belongs to this account.
      clearOrphanedPurchaseHint();
      trackEvent('paid_access_gate_restore_succeeded', {});
      refreshSubscription();
      // Optimistic pass-through, same as a purchase: the entitlement
      // snapshot may land a beat behind the restore.
      setPurchasedThisSession(true);
    } else {
      // The hint was wrong or stale — clear it so we don't keep promising a
      // membership the store can't find, and fall back to the standard sell.
      clearOrphanedPurchaseHint();
      trackEvent('paid_access_gate_restore_failed', {});
      setRestoreFailed(true);
    }
  };

  // Restore leads on either signal: the device-local breadcrumb (fast,
  // fallible) or the store account's own answer (authoritative, async). A
  // failed restore clears both claims and falls back to the standard sell.
  const showRestoreLead = (restoreHint || storeCheck === 'owned') && !restoreFailed;

  return (
    <div className="min-h-dvh-safe bg-sand flex flex-col items-center justify-center p-6 text-growth font-sans">
      <div className="w-full max-w-sm text-center space-y-4">
        <img
          src="/assets/logos/enough_wordmark_green.png"
          alt="enough"
          className="w-40 h-auto mx-auto"
        />
        <h1 className="text-2xl font-serif">
          {emphasizeBrand(showRestoreLead ? 'Your membership is waiting' : 'Continue with membership')}
        </h1>
        {showRestoreLead ? (
          <p className="text-sm text-growth/70">
            {/* Covers both roads here: a pre-login purchase on this device,
                and an existing membership surfacing on a new device or
                reinstall (the store-account query). */}
            There&apos;s already an active membership on this phone&apos;s app
            store account. Restore purchases to bring it to this account —
            you won&apos;t be charged twice.
          </p>
        ) : (
        // One line honest for BOTH audiences at this gate: a brand-new web
        // signup arriving from the carousel (never had a trial) and a lapsed
        // member whose trial/membership ended. There's no reliable client
        // signal to tell them apart today (expirationDate only comes back for
        // ACTIVE entitlements), so never assert "your trial ended" flatly.
        <p className="text-sm text-growth/70">
          {emphasizeBrand(
            'You don’t have an active membership — maybe your free trial ended, or you haven’t started one yet. Membership covers the real cost of syncing and analyzing your accounts.',
          )}
        </p>
        )}
        {restoreFailed && (
          <p className="text-sm text-growth/70">
            We couldn&apos;t find a membership to restore on this device. You can
            start one below — or if you subscribed on another device, restore
            from there.
          </p>
        )}
        {!canSellHere && (
          // Fail-closed state: web with the Stripe prices unconfigured. Honest
          // about the outage, no purchase path to offer — support + log out
          // are the escapes. Native users never see this (stores always sell).
          <p className="text-sm text-growth/70">
            Membership options aren&apos;t available right now — please try again
            soon. If you already have a membership, contact support and
            we&apos;ll get you back in.
          </p>
        )}
        {showRestoreLead && (
          <button
            onClick={handleRestore}
            disabled={restoring}
            className="w-full py-4 rounded-xl bg-growth text-cloud font-medium hover:bg-growth/90 transition-colors disabled:opacity-60"
          >
            {restoring ? 'Restoring…' : 'Restore purchases'}
          </button>
        )}
        {/* No sell path while the Restore lead is up (Justin, 2026-07-21):
            with the device's store account owning the sub, the paywall is a
            purchase the store will refuse — a dead end, not an option. The
            stale-hint case loses nothing: a failed restore clears the hint
            and this button reappears with the standard sell. */}
        {canSellHere && !showRestoreLead && (
          <button
            onClick={() => {
              purchasedRef.current = false;
              // No second funnel impression here — the gate APPEARANCE fired
              // it (impressionFired effect). Re-firing per reopen inflated
              // impressions relative to purchase_started for the same gate.
              setPaywallOpen(true);
            }}
            className="w-full py-4 rounded-xl bg-growth text-cloud font-medium hover:bg-growth/90 transition-colors"
          >
            See membership options
          </button>
        )}
        {/* Web only (renders nothing on native): the member whose subscription
            is an unrestored App Store / Play purchase has no restore here —
            point them to the phone before they double-bill via Stripe. */}
        <WebRestoreGuidance />
        <button
          onClick={() => window.open('https://enough.app/contact-us', '_blank')}
          className="w-full text-center text-sm text-growth/50 hover:text-growth transition-colors"
        >
          Contact support
        </button>
        <button
          onClick={() => logout()}
          className="w-full text-center text-sm text-growth/50 hover:text-growth transition-colors"
        >
          Log out
        </button>
      </div>

      {canSellHere && (
        <SpendingToolsPaywall
          isOpen={paywallOpen}
          onClose={handleClose}
          surface={paywallSurface}
          // The funnel surface has no dismiss, so the backdrop's Log out below
          // is unreachable while the paywall is up — the header escape is the
          // only exit that isn't a purchase. (The app surface ignores this;
          // its X returns to the backdrop.)
          onSignOut={() => logout()}
          onSuccess={handleSuccess}
          onPurchaseStart={() =>
            recordPaywallFunnelEvent(currentUser, 'purchase_started', { source: GATE_SOURCE })
          }
        />
      )}
    </div>
  );
};
